// App.js
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "./hooks/useAuth";
import { useUIHelpers } from "./hooks/useUIHelpers"; // Import the UI helpers hook
import { componentMap, roles } from "./routes"; // Import componentMap and roles
import NotFound from "./components/Layouts/NotFound";
import UnderMaintenance from "./components/Layouts/UnderMaintenance";
import { useEffect, useState } from "react";
import axios from "axios";
import CustomLoader from "./components/CustomLoader";
import {
  clearRedux,
  emailVerificationSuccess,
  loginSuccess,
} from "./store/actions";
import { useDispatch } from "react-redux";
import { message } from "antd";
import APICaller from "./utils/APICaller";
import CryptoJS from "crypto-js";
import { encryptData } from "./components/Constants";
import LogoutHandler from "./view/LogoutHandler";
import LogoutSyncHandler from "./view/LogoutHandler/logoutSyncHandler";

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname?.split("/");
  const navigateHome = () => {
    navigate("/dashboard");
  };
  // Maintenance Mode Flag (You could replace this with a global state or an API call)
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [serverStatus, setServerStatus] = useState("up");
  const [isCheckingServerStatus, setIsCheckingServerStatus] = useState(true); // Flag to indicate if server status is being checked

  // Use the custom hook for authentication and UI helpers
  const { loading, isLoggedIn, userRole, accessToken } = useAuth(
    navigate,
    location
  );

  // Get the accessible routes based on the user's role
  const hasAccess = (role) => roles[role] || [];
  const accessibleRoutes = userRole ? hasAccess(userRole) : [];

  // Call the UI helpers hook
  useUIHelpers();
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token"); // Get the token from URL

    if (token) {
      validateToken(token);
    } else {
      const logoutReq = pathName[1] === "logout" ? true : false;

      if (!accessToken && !logoutReq) {
        window.location.href = process.env.REACT_APP_SSO_LOGIN_REDIRECT_URI;
      }
      if (location.pathname === "/" && accessToken) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, []);

  const deriveKey = (password, salt) => {
    return CryptoJS.PBKDF2(password, salt, {
      keySize: 256 / 32,
      iterations: 65536,
    });
  };

  // Function to decrypt the data
  const decrypt = (encryptedData, encryptionKey) => {
    try {
      // Decode the base64 encrypted data into a byte array
      const combined = CryptoJS.enc.Base64.parse(encryptedData);

      // Extract salt (first 16 bytes)
      const salt = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));

      // Extract IV (next 16 bytes)
      const iv = CryptoJS.lib.WordArray.create(combined.words.slice(4, 8));

      // The remaining part is the encrypted content
      const encrypted = CryptoJS.lib.WordArray.create(combined.words.slice(8));

      // Derive the secret key using PBKDF2 with the provided salt
      const secretKey = deriveKey(encryptionKey, salt);

      // Decrypt the data using AES with the derived key and IV
      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: encrypted },
        secretKey,
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7, // Ensure proper padding
        }
      );

      // Return the decrypted data as a UTF-8 string
      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

      // Check if decryption is successful
      if (!decryptedText) {
        throw new Error("Decrypted data is empty or invalid UTF-8.");
      }

      return decryptedText;
    } catch (error) {
      console.error("Decryption error:", error.message);
      return null;
    }
  };

  //API to validate token
  const validateToken = async (token) => {
    //Saving user device info
    let deviceInfo = "";
    try {
      const response = await axios.get(process.env.REACT_APP_IPIFY);

      if (response) {
        const userAgent = navigator.userAgent; // Accessing navigator
        const screenWidth = window.innerWidth; // Screen width
        const screenHeight = window.innerHeight; // Screen height
        const ip = response.data.ip;
        deviceInfo = {
          userAgent,
          screenWidth,
          screenHeight,
          ip,
        };
      }
    } catch (error) {
      console.error("Error fetching IP:", error);
    }
    try {
      const endpoint = "/validateToken";
      const method = "POST";
      const payload = {
        auth: token,
        device: deviceInfo,
      };
      await APICaller("", token, endpoint, method, payload, "")
        .then((response) => {
          if (response.statusCode === 200) {
            // Decrypt the encrypted response data
            const decryptedData = decrypt(
              response.respnse,
              process.env.REACT_APP_SSO_ENCRYPTION_KEY
            );

            const decryptedObject = JSON.parse(decryptedData);
            const getAccessTokenExpiresOn = decryptedObject.expiryTime;

            if (decryptedObject) {
              // Dispatch action to update Redux state with tokens
              dispatch(
                loginSuccess({
                  lIU: decryptedObject?.user.name,
                  accessToken: decryptedObject?.accessToken,
                  accessTokenExpiresOn: getAccessTokenExpiresOn,
                })
              );

              dispatch(
                emailVerificationSuccess({
                  isLoggedIn: true,
                  lIUId: decryptedObject?.user?._id,
                  role: decryptedObject?.user?.roleID?.name,
                  name: decryptedObject?.user?.name,
                  empID: decryptedObject?.user?.empID,
                  profileImage: decryptedObject?.user?.profilePicUrl,
                  userInfo: encryptData({
                    user: decryptedObject?.user,
                    userDepartment: decryptedObject?.departmentNames,
                    userDepartmentCode: decryptedObject?.departmentCode,
                  }),
                  logoutSuccessUrl:
                    decryptedObject?.decryptedData?.logoutSuccessUrl,
                })
              );
              response.statusCode === 200 && navigateHome();
            }
          }
        })
        .catch((error) => {});
    } catch (error) {
      showNotification(
        "The email you used does not exist in our system. Please logout and try logging in with a different account."
      );

      // Trigger the MSAL login popup again to allow the user to log in with a different email
      clearAndLogout();
    }
  };

  //To show notification using Ant Design's notification component
  const showNotification = (errorMsg, moreThan = "no") => {
    message.error({
      content: (
        <div style={{ whiteSpace: "pre-line" }}>
          {moreThan === "yes" ? (
            <strong>Errors occurred:</strong>
          ) : (
            <strong>An error occurred:</strong>
          )}
          <p>{errorMsg}</p> {/* This ensures line breaks are respected */}
        </div>
      ),
      duration: 5, // duration in seconds
      className: "error-message", // Add any custom styles you need
    });
  };

  const clearAndLogout = async () => {
    dispatch(clearRedux());
    localStorage.setItem("logout", Date.now()); // Triggers storage event
    window.location.href = process.env.REACT_APP_SSO_LOGIN_REDIRECT_URI;
  };

  useEffect(() => {
    // Function to check server status (whether the backend is up or not)
    const checkMaintenanceStatus = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_BACKEND + "/checkServerStatus"
        );
        setServerStatus(response.data.status);

        // If the server is down or in maintenance, navigate to the /underMaintenance route
        if (response.data.status === "Error") {
          setIsUnderMaintenance(true);
        }
      } catch (error) {
        setServerStatus("Error");
        setIsUnderMaintenance(true);
      } finally {
        setIsCheckingServerStatus(false); // Finished checking server status
      }
    };

    checkMaintenanceStatus();
  }, []);

  // Show loading while the server status is being checked
  if (isCheckingServerStatus) {
    return <CustomLoader showLoader="true" />; // Show loading until server status is confirmed
  }

  if (isUnderMaintenance) {
    document.title = "Under Maintenance"; // You can replace this with any desired title
    return (
      <>
        <UnderMaintenance />
        <Navigate to="/underMaintenance" replace />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route index element={isLoggedIn && componentMap["/dashboard"]} />
        {accessibleRoutes.length === 0 ? (
          <Route path="*" element={<Navigate to="/" replace />} />
        ) : (
          accessibleRoutes.map((route) => (
            <Route key={route} path={route} element={componentMap[route]} />
          ))
        )}
        <Route path="/logout/:empID" element={<LogoutHandler />} />
        <Route
          path="*"
          element={<Navigate to={componentMap.noRoute} replace />}
        />
        <Route path="/404" element={<NotFound />} />
      </Routes>
    </>
  );
};

export default App;
